import dayjs, { Dayjs } from "dayjs";
import advancedFormat from 'dayjs/plugin/advancedFormat';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import isoWeeksInYear from 'dayjs/plugin/isoWeeksInYear';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import timezone from 'dayjs/plugin/timezone';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(dayOfYear)
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat)
dayjs.extend(weekday)
dayjs.extend(updateLocale)
dayjs.extend(isoWeeksInYear)
dayjs.extend(isLeapYear)

// @ts-ignore
dayjs.updateLocale('en', {
    weekStart: 1
});

export function datetime(data: any = Date.now()): Datetime {
    return new Datetime(data);
}

export function getStartOfWeek(year: string | number, week: string | number): Datetime {
    const date = dayjs(`${year}-6-6`).week(+week).startOf('week');
    return new Datetime(date);
}

export function getEndOfWeek(year: string | number, week: string | number): Datetime {
    const date = dayjs(`${year}-6-6`).week(+week).endOf('week');
    return new Datetime(date);
}

class Datetime {
    private readonly data: any = dayjs();

    constructor(data?: any) {
        this.data = dayjs(data);
        return this;
    }

    format(type: string) {
        return this.data.format(type);
    }

    add(time: number, unit: any) {
        return new Datetime(this.data.add(time, unit));
    }

    subtract(time: number, unit: any) {
        return new Datetime(this.data.subtract(time, unit));
    }

    diff(time: Date | number, unit: dayjs.OpUnitType = 'millisecond') {
        const data = datetime(time);
        return this.data.diff(data.value, unit);
    }

    startOf(unit: any) {
        return new Datetime(this.data.startOf(unit));
    }

    endOf(unit: any) {
        return new Datetime(this.data.endOf(unit));
    }

    toISOString() {
        return this.data.toISOString();
    }

    set(data: { [id: string]: number | string }) {
        let time = this.data;
        const { week, ...args } = data;
        if (week) {
            const year = data.year || time.year();
            time = time.set('year', +year);
            time = time.isoWeek(+week);
            delete args.year;
        }
        Object.keys(args).forEach((key: any) => {
            switch (key) {
                case 'day':
                    time = time.weekday(+data[key]);
                    break;
                default:
                    time = time.set(key, +args[key]);
            }
        })

        return new Datetime(time);
    }

    get isValid() {
        return this.data.isValid();
    }

    get day() {
        return this.data.weekday();
    }

    get date() {
        return this.data.date();
    }

    get month() {
        return this.data.month();
    }

    get week() {
        const value: any = this.data;
        return value.isoWeek();
    }

    get year() {
        return this.data.year();
    }

    get weekYear() {
        return this.data.isoWeekYear();
    }

    get weeksInYear() {
        return this.data.isoWeeksInYear();
    }

    get hour() {
        return this.data.hour();
    }

    get minute() {
        return this.data.minute();
    }

    get second() {
        return this.data.second();
    }

    get time() {
        return this.data.valueOf();
    }

    get utcOffset() {
        return this.data.utcOffset();
    }

    get UTCText() {
        return this.data.utc().format();
    }

    get value(): Dayjs {
        return this.data;
    }
}

export default Datetime
